import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationContext } from '../contexts/NotificationContext';
import { Button } from './ui/button';
import { Bell, CheckCircle, XCircle, Users, ArrowRight, Wifi, WifiOff } from 'lucide-react';

const EVENT_CONFIG = {
  new_assignment: { icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'New Assignment Available' },
  assignment_taken: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Assignment Accepted' },
  assignment_cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Assignment Cancelled' },
};

export default function NotificationBell() {
  const { connected, notifications, markAllRead, clearAll } = useNotificationContext();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = (n) => {
    if (n.type === 'new_assignment') navigate('/interpreter');
    else if (n.type === 'assignment_taken') navigate('/interpreter');
    else if (n.type === 'assignment_cancelled') navigate('/admin/assignment-board');
    setOpen(false);
  };

  const formatTime = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button variant="ghost" size="icon" onClick={() => setOpen(p => !p)} className="relative" data-testid="notification-bell">
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[hsl(var(--brand))] text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-lg border bg-card shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="px-3 py-2 border-b flex items-center justify-between bg-card">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Notifications</h3>
              {connected ? (
                <span className="flex items-center gap-1 text-[10px] text-green-500"><Wifi className="w-3 h-3" />Live</span>
              ) : (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><WifiOff className="w-3 h-3" />Offline</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={markAllRead} data-testid="mark-all-read">
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2 text-muted-foreground" onClick={clearAll} data-testid="clear-notifications">
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="no-notifications">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                No notifications yet
              </div>
            ) : (
              notifications.map(n => {
                const config = EVENT_CONFIG[n.type] || EVENT_CONFIG.new_assignment;
                const Icon = config.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-3 py-2.5 border-b last:border-b-0 hover:bg-accent/50 transition-colors flex items-start gap-3 ${!n.read ? 'bg-[hsl(var(--brand))]/5' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium ${!n.read ? '' : 'text-muted-foreground'}`}>{config.label}</p>
                      {n.data?.target_language && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Language: {n.data.target_language?.toUpperCase()}
                          {n.data.duration_minutes ? ` · ${n.data.duration_minutes}min` : ''}
                        </p>
                      )}
                      {n.data?.accepted_by && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">By: {n.data.accepted_by}</p>
                      )}
                      {n.data?.cancellation_fee > 0 && (
                        <p className="text-[11px] text-red-500 mt-0.5">Fee: {n.data.cancellation_fee} credits</p>
                      )}
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{formatTime(n.timestamp)}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-[hsl(var(--brand))] flex-shrink-0 mt-2" />}
                    <ArrowRight className="w-3 h-3 text-muted-foreground/30 flex-shrink-0 mt-2" />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
